'use client';

import { useState, type FC } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@wwe-portal/ui/components/dropdown';
import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { cn } from '@wwe-portal/ui/lib/lib';
import { Each } from '../../../components/utils';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';

export interface SelectItemProps {
  id: string;
  title: string;
  iconUrl?: string;
}

export interface RosterAttributeSelectProps {
  title: string;
  titleClassName?: string;
  items: SelectItemProps[];
  align?: 'start' | 'end' | 'center';
  innerWidth?: number;
  selectedId: string | null;
  onSelectItem: (selectedId: string) => void;
  clearFilterText?: string;
  onClearFilter?: () => void;
  container?: HTMLElement | null;
  hasArrowIcon?: boolean;
  highlightSelectedItem?: boolean;
  triggerClassName?: string;
}

export const RosterAttributeSelect: FC<RosterAttributeSelectProps> = ({
  title,
  titleClassName,
  align,
  items,
  selectedId,
  innerWidth,
  onSelectItem,
  clearFilterText,
  onClearFilter,
  container,
  hasArrowIcon,
  highlightSelectedItem = true,
  triggerClassName,
}) => {
  const selectedItem = items.find((item) => item.id === selectedId);
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <ClickableDiv
          className={cn(
            'border-back200 bg-back300 text-text600 drop-shadow-card relative flex h-[46px] items-center justify-center border-0 border-l-[1px] border-solid px-5 py-2.5 text-center text-sm font-medium hover:border-[#343961] hover:bg-[#343961] hover:text-[#b4bae9]',
            {
              'border-l-0': align === 'start',
              'bg-back200': open,
            },
            triggerClassName
          )}
        >
          {selectedItem?.iconUrl && (
            <img
              alt={selectedItem.title}
              className="mr-1 inline"
              height={16}
              src={selectedItem.iconUrl}
              width={16}
            />
          )}
          <span
            className={cn(
              {
                'text-[#00BFFF]': selectedItem != null,
              },
              titleClassName
            )}
          >
            {title}
          </span>
          {hasArrowIcon && (
            <div className="absolute inset-y-0 right-0 p-4">
              <IcoMoonIcon
                iconName="arrow"
                className={cn('text-inlineTextColor18 size-4')}
              />
            </div>
          )}
        </ClickableDiv>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className="bg-back200 text-text600 w-[--radix-popper-anchor-width]"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
        }}
        portalProps={{
          container,
        }}
        style={{
          width: innerWidth,
        }}
      >
        <div className="no-scrollbar pointer-events-auto max-h-[360px] overflow-y-auto">
          <Each
            data={items}
            render={(item) => (
              <DropdownMenuItem
                key={item.id}
                className={cn(
                  'hover:bg-back300 cursor-pointer rounded-xl px-2 capitalize',
                  {
                    'text-yellow500':
                      selectedId === item.id && highlightSelectedItem,
                  }
                )}
                onClick={() => onSelectItem(item.id)}
              >
                {item.iconUrl && (
                  <img
                    alt={item.title}
                    className="mr-[10px]"
                    height={28}
                    src={item.iconUrl}
                    width={28}
                  />
                )}
                {item.title.replace(/\./g, ' ')}
              </DropdownMenuItem>
            )}
          />
        </div>
        {onClearFilter && clearFilterText && (
          <>
            <DropdownMenuSeparator className="mt-4" />
            <DropdownMenuItem
              className="hover:bg-back300 h-[52px] w-full cursor-pointer rounded-xl px-2"
              onClick={onClearFilter}
            >
              {clearFilterText}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
